import { PermissionCode, PermissionType, PlanPriceInterval } from '@/graphql/generated/types.generated';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { monetaryFormat } from '@/utils/monetary-format';
import { FEATURED_CODES } from './constants';
import { PLANS_YEARLY_DISCOUNT } from '@/constants';
import type { MappedPlan } from './utils/map-graphql-to-calculator.util';

interface PlanCardProps {
    plan: MappedPlan;
    interval: PlanPriceInterval;
    selected: boolean;
    onSelect: (code: string) => void;
    featuredCodes?: PermissionCode[];
}

export const PlanCard = ({ plan, interval, selected, onSelect, featuredCodes = FEATURED_CODES }: PlanCardProps) => {
    const { t } = useTranslation();
    const displayPrice = interval === PlanPriceInterval.Year ? plan.yearlyMonthlyPrice : plan.monthlyPrice;
    const yearlySaving = Math.round((1 - PLANS_YEARLY_DISCOUNT) * 100);

    const featuredFeatures = featuredCodes.map((code) => plan.features.find((f) => f.code === code)).filter(
        Boolean,
    );

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                cursor: 'pointer',
                border: '2px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                transition: 'border-color 0.2s',
                position: 'relative',
                overflow: 'visible',
                '&:hover': {
                    borderColor: selected ? 'primary.main' : 'action.focus',
                },
            }}
            onClick={() => onSelect(plan.code)}
        >
            {plan.mostPopular && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%) translateY(-50%)',
                        zIndex: 1,
                    }}
                >
                    <Chip
                        label={t('billing.calculator.plan-card.most-popular')}
                        size="small"
                        sx={{ bgcolor: blue[100], color: blue[700], fontWeight: 600, fontSize: '0.7rem' }}
                    />
                </Box>
            )}

            <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
                <Stack spacing={2}>
                    {/* Header */}
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            {plan.name}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.5 }}
                        >
                            {t(`str.subs.plan.${plan.code}.description`)}
                        </Typography>
                    </Box>

                    {/* Price */}
                    <Box>
                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                            <Typography variant="h4" fontWeight={800}>
                                {monetaryFormat(displayPrice, plan.currency)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('billing.calculator.per-month')}
                            </Typography>
                        </Stack>
                        {interval === PlanPriceInterval.Year ? (
                            <Typography variant="caption" color="success.main" fontWeight={600}>
                                {t('billing.calculator.plan-card.save-yearly', { percent: yearlySaving })}
                            </Typography>
                        ) : (
                            <Typography variant="caption" color="text.disabled">
                                {t('billing.calculator.plan-card.save-yearly-alternative', {
                                    price: monetaryFormat(plan.yearlyMonthlyPrice, plan.currency),
                                    percent: Math.round((1 - PLANS_YEARLY_DISCOUNT) * 100),
                                })}
                            </Typography>
                        )}
                    </Box>

                    {/* CTA */}
                    <Button
                        variant={selected ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(plan.code);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {selected
                            ? t('billing.calculator.plan-card.action.selected')
                            : t('billing.calculator.plan-card.action.select')}
                    </Button>

                    {/* Top features preview */}
                    <Stack spacing={1}>
                        {featuredFeatures.map((feature) => {
                            if (!feature) return null;
                            const enabled = feature.value > 0;
                            return (
                                <Stack key={feature.code} direction="row" spacing={1} alignItems="center">
                                    {enabled ? (
                                        <CheckRounded
                                            sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }}
                                        />
                                    ) : (
                                        <CloseRounded
                                            sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                                        />
                                    )}
                                    <Typography
                                        variant="body2"
                                        color={enabled ? 'text.secondary' : 'text.disabled'}
                                    >
                                        {feature.type === PermissionType.Numeric
                                            ? `${feature.displayValue} ${t(feature.label)}`
                                            : t(feature.label)}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};
